import { useMemo, useState } from "react";
import { FormikProvider, useFormik } from "formik";
import { useScheduleTemplate } from "@api/scheduleTemplate.api";
import { useApplyScheduleWithLinkedConflict } from "./linkedConflict/useApplyScheduleWithLinkedConflict";
import { ApplyScheduleTemplateBody } from "@common/schemas/domains/accounting/scheduleApplication.schema";
import { ScheduleTemplateListItem } from "@common/schemas/domains/accounting/scheduleTemplate.schema";
import { hasSpecificDateAnchor } from "@common/helpers/domains/accounting/scheduleAnchors";
import { ScheduleSourceType } from "@common/types/domains/accounting/schedule";
import { Modal } from "@design-system/Modal/Modal";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { VStack } from "@design-system/VStack/VStack";
import { useEntitiesById } from "@hooks/useEntities";
import { submitHelper, validateFormikWithZodSchema } from "@helpers/form";
import { ScheduleManualApplyContent } from "./manual/ScheduleManualApplyContent";
import { ScheduleApplyFields } from "./ScheduleApplyFields";
import { ScheduleApplyPreview } from "./ScheduleApplyPreview";
import { ScheduleApplyPlainContent } from "./ScheduleApplyPlainContent";
import { ApplySourceDestination } from "./ScheduleApplySourcePicker";
import {
    ApplySourceType,
    buildScheduleApplyFormSchema,
    buildScheduleApplyPayload,
    getInitialScheduleApplyValues,
    ScheduleApplyFormValues,
} from "./scheduleApplyFormHelpers";
import { buildAccruedBilling } from "./scheduleApplyAccruedHelpers";
import { useScheduleApplyPreview } from "./useScheduleApplyPreview";
import { useScheduleLockedPeriodsDialog } from "./useScheduleLockedPeriodsDialog";

enum ApplyStep {
    SourcePicker,
    Fields,
}

interface ScheduleApplyModalProps {
    show: boolean;
    template: ScheduleTemplateListItem | null;
    onHide: () => void;
    onNavigate: (destination: ApplySourceDestination) => void;
    /** Fires after a new schedule landed, with its application id. */
    onApplied?: (applicationId: string) => void;
}

function getInitialEntityId(
    template: ScheduleTemplateListItem | null,
): number | null {
    return template?.entityIds.length === 1 ? template.entityIds[0] : null;
}

interface ApplyFlow {
    step: ApplyStep;
    sourceType: ApplySourceType | null;
    isExpanded: boolean;
    /** The entity picked before switching source; null keeps the template's. */
    entityId: number | null;
}

const INITIAL_FLOW: ApplyFlow = {
    step: ApplyStep.SourcePicker,
    sourceType: null,
    isExpanded: false,
    entityId: null,
};

/**
 * The dialog unmounts its body when it closes, so every opening starts fresh
 * from `template`. A manual source switches the dialog to the standard
 * variant, which remounts the body, so the shell owns the step, the source
 * and the entity picked so far.
 */
export const ScheduleApplyModal: React.FC<ScheduleApplyModalProps> = ({
    show,
    onHide,
    ...bodyProps
}) => {
    const [flow, setFlow] = useState(INITIAL_FLOW);
    const isManual =
        flow.step === ApplyStep.Fields &&
        flow.sourceType === ScheduleSourceType.manual;
    const handleHide = () => {
        setFlow(INITIAL_FLOW);
        onHide();
    };
    return (
        <Modal
            show={show}
            onHide={handleHide}
            variant={isManual ? "standard" : "plain"}
            size={isManual ? "lg" : "md"}
            fullscreen={isManual && flow.isExpanded}
        >
            <ScheduleApplyModalBody
                {...bodyProps}
                onHide={handleHide}
                flow={flow}
                onFlowChange={setFlow}
            />
        </Modal>
    );
};

const ScheduleApplyModalBody: React.FC<
    Omit<ScheduleApplyModalProps, "show"> & {
        flow: ApplyFlow;
        onFlowChange: (flow: ApplyFlow) => void;
    }
> = ({ template, onHide, onNavigate, onApplied, flow, onFlowChange }) => {
    const { step, sourceType, isExpanded } = flow;
    const { toast, error } = useToaster();
    const entitiesById = useEntitiesById({ onlyBusiness: true });
    const templateQuery = useScheduleTemplate(template?.id ?? null);
    const { applyWithLinkedConflict, linkedConflictModal, isApplying } =
        useApplyScheduleWithLinkedConflict();
    const showLockedPeriods = useScheduleLockedPeriodsDialog();
    const initialEntityId = flow.entityId ?? getInitialEntityId(template);

    const requiresSpecificDate = hasSpecificDateAnchor(
        templateQuery.data?.components ?? [],
    );

    const submitPayload = async (payload: ApplyScheduleTemplateBody) => {
        if (!template) {
            return;
        }
        const finishApply = (applicationId: string | null) => {
            onHide();
            if (applicationId) {
                onApplied?.(applicationId);
            }
        };
        try {
            const result = await applyWithLinkedConflict({
                templateId: template.id,
                payload,
                onApplied: (applied) => finishApply(applied.applicationId),
            });
            if (result.outcome === "awaitingChoice") {
                return;
            }
            toast("Schedule applied");
            finishApply(result.applicationId);
        } catch (submitError) {
            if (showLockedPeriods(submitError)) {
                return;
            }
            error(
                submitError instanceof Error
                    ? submitError.message
                    : "Failed to apply schedule",
            );
        }
    };

    const formik = useFormik<ScheduleApplyFormValues>({
        initialValues: getInitialScheduleApplyValues(initialEntityId),
        validate: validateFormikWithZodSchema(
            buildScheduleApplyFormSchema({ sourceType, requiresSpecificDate }),
        ),
        onSubmit: submitHelper({
            handler: async (values) => {
                const payload = buildScheduleApplyPayload({
                    values,
                    sourceType,
                });
                if (payload) {
                    await submitPayload(payload);
                }
            },
        }),
    });
    const { resetForm } = formik;

    const selectSource = (nextSourceType: ApplySourceType) => {
        if (nextSourceType !== sourceType) {
            resetForm({
                values: {
                    ...getInitialScheduleApplyValues(initialEntityId),
                    entityId: formik.values.entityId,
                },
            });
        }
        onFlowChange({
            step: ApplyStep.Fields,
            sourceType: nextSourceType,
            isExpanded: false,
            entityId: formik.values.entityId,
        });
    };

    const goBack = () =>
        onFlowChange({
            ...flow,
            step: ApplyStep.SourcePicker,
            isExpanded: false,
        });

    const preview = useScheduleApplyPreview({
        template: templateQuery.data,
        amount: formik.values.amount,
        termStart: formik.values.termStart,
        sourceDate: formik.values.sourceDate || null,
        specificDate: formik.values.specificDate,
        billing: buildAccruedBilling({
            sourceDate:
                sourceType === ScheduleSourceType.invoice
                    ? formik.values.sourceDate || null
                    : null,
            amount: formik.values.amount,
            noun: "Invoice",
        }),
    });
    const canSubmit =
        template != null &&
        templateQuery.isSuccess &&
        preview.errorMessage === null &&
        preview.isSettled &&
        buildScheduleApplyFormSchema({
            sourceType,
            requiresSpecificDate,
        }).safeParse(formik.values).success;
    const isSubmitting = isApplying || templateQuery.isLoading;
    const isManualFields =
        step === ApplyStep.Fields && sourceType === ScheduleSourceType.manual;

    const handleNavigate = (destination: ApplySourceDestination) => {
        onHide();
        onNavigate(destination);
    };

    const entityIds = useMemo(() => template?.entityIds ?? [], [template]);

    const renderContent = () => {
        if (isManualFields) {
            return (
                <ScheduleManualApplyContent
                    template={templateQuery.data}
                    entityIds={entityIds}
                    entitiesById={entitiesById}
                    isExpanded={isExpanded}
                    onExpandedChange={(nextIsExpanded) =>
                        onFlowChange({ ...flow, isExpanded: nextIsExpanded })
                    }
                    onCancel={onHide}
                    isSubmitting={isSubmitting}
                    onSubmit={(payload) => void submitPayload(payload)}
                />
            );
        }
        if (step === ApplyStep.SourcePicker) {
            return (
                <ScheduleApplyPlainContent
                    step="source"
                    scheduleType={template?.type ?? null}
                    onSelectSource={selectSource}
                    onNavigate={handleNavigate}
                    onCancel={onHide}
                />
            );
        }
        return (
            <ScheduleApplyPlainContent
                step="fields"
                fields={
                    <VStack gap={16}>
                        <ScheduleApplyFields
                            entityIds={entityIds}
                            entitiesById={entitiesById}
                            requiresSpecificDate={requiresSpecificDate}
                            template={templateQuery.data}
                        />
                        <ScheduleApplyPreview preview={preview} />
                    </VStack>
                }
                onBack={goBack}
                isSubmitDisabled={!canSubmit}
                isSubmitting={isSubmitting}
                onSubmit={() => void formik.submitForm()}
            />
        );
    };

    return (
        <>
            <FormikProvider value={formik}>{renderContent()}</FormikProvider>
            {linkedConflictModal}
        </>
    );
};
